<template>
  <!--
    数据贯通成熟度自评定位图（内联 SVG）
    沿用 v1.0.0 的 MaturityChart.vue 版式，气泡坐标与配色由
    src/data/selfAssessment.js 的 SELF_LEVEL_NODES 驱动，新增亚级只需改数据。
  -->
  <svg
    class="maturity-chart"
    viewBox="0 0 1000 840"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="数据贯通成熟度自评定位图"
    font-family="'PingFang SC','Microsoft YaHei','Noto Sans SC',sans-serif"
  >
    <defs>
      <linearGradient id="mc-bg" x1="0" y1="1" x2="1" y2="0">
        <stop offset="0" stop-color="#f8fafc" />
        <stop offset="1" stop-color="#eef4ff" />
      </linearGradient>
      <marker id="mc-arrow-h" viewBox="0 0 10 10" refX="9" refY="5"
              markerWidth="8" markerHeight="8" orient="auto-start-reverse">
        <path d="M0,0 L10,5 L0,10 z" fill="#475569" />
      </marker>
      <marker id="mc-arrow-v" viewBox="0 0 10 10" refX="9" refY="5"
              markerWidth="8" markerHeight="8" orient="auto-start-reverse">
        <path d="M0,0 L10,5 L0,10 z" fill="#64748b" />
      </marker>
      <filter id="mc-pill-shadow" x="-30%" y="-30%" width="160%" height="160%">
        <feDropShadow dx="0" dy="2" stdDeviation="2.5"
                      flood-color="#0f172a" flood-opacity="0.18" />
      </filter>
    </defs>

    <rect width="1000" height="840" fill="url(#mc-bg)" />

    <text x="500" y="38" text-anchor="middle" font-size="26" font-weight="700" fill="#0f172a">
      数据贯通成熟度自评定位图
    </text>
    <text x="500" y="60" text-anchor="middle" font-size="13" fill="#64748b">
      左下（人治 · 非结构化）→ 右上（自治 · 智能化）
    </text>

    <!-- 纵向网格 -->
    <g stroke="#cbd5e1" stroke-width="1" stroke-dasharray="5 5">
      <line x1="320" y1="150" x2="320" y2="660" />
      <line x1="430" y1="150" x2="430" y2="660" />
      <line x1="540" y1="150" x2="540" y2="660" />
      <line x1="650" y1="150" x2="650" y2="660" />
      <line x1="760" y1="150" x2="760" y2="660" />
    </g>

    <!-- 横向网格 -->
    <g stroke="#cbd5e1" stroke-width="1" stroke-dasharray="5 5">
      <line x1="200" y1="200" x2="930" y2="200" />
      <line x1="200" y1="300" x2="930" y2="300" />
      <line x1="200" y1="390" x2="930" y2="390" />
      <line x1="200" y1="480" x2="930" y2="480" />
      <line x1="200" y1="600" x2="930" y2="600" />
    </g>

    <!-- 纵轴：CDE 环境 -->
    <line x1="590" y1="660" x2="590" y2="134" stroke="#64748b"
          stroke-width="2" marker-end="url(#mc-arrow-v)" />
    <g text-anchor="middle" fill="#475569">
      <text x="590" y="98" font-size="17" font-weight="700" fill="#1e40af">CDE 环境</text>
      <text x="590" y="120" font-size="15" font-weight="600">智能化生产环境</text>
      <text x="590" y="690" font-size="15" font-weight="600">传统生产环境</text>
      <text x="590" y="714" font-size="17" font-weight="700" fill="#92400e">无 CDE 环境</text>
    </g>

    <!-- 横轴：人 / 机 -->
    <line x1="225" y1="480" x2="900" y2="480" stroke="#475569"
          stroke-width="2" marker-end="url(#mc-arrow-h)" />
    <g fill="#334155" font-weight="700">
      <text x="232" y="462" font-size="20" text-anchor="start">人</text>
      <text x="232" y="504" font-size="16" text-anchor="start">无平台</text>
      <text x="893" y="462" font-size="20" text-anchor="end">机</text>
      <text x="893" y="504" font-size="16" text-anchor="end">有平台</text>
    </g>

    <!-- 纵轴刻度文字 -->
    <g text-anchor="end" font-size="16" font-weight="600" fill="#334155">
      <text x="190" y="206">智能化</text>
      <text x="190" y="306">自动化</text>
      <text x="190" y="396">结构化</text>
      <text x="190" y="486">信息化</text>
      <text x="190" y="606">非结构化</text>
    </g>

    <!-- 等级气泡（可点击选择） -->
    <g font-size="14" font-weight="700" text-anchor="middle">
      <g
        v-for="n in nodes"
        :key="n.code"
        class="pill"
        :class="{ 'is-active': n.code === modelValue }"
        @click="$emit('select', n.code)"
      >
        <!-- 选中态外圈 -->
        <ellipse
          v-if="n.code === modelValue"
          :cx="n.cx" :cy="n.cy" rx="42" ry="27"
          fill="none" :stroke="n.stroke" stroke-width="2" stroke-dasharray="4 3"
        />
        <ellipse
          :cx="n.cx" :cy="n.cy"
          :rx="n.code === modelValue ? 38 : 34"
          :ry="n.code === modelValue ? 22 : 19"
          :fill="n.fill" :stroke="n.stroke" :stroke-width="n.code === modelValue ? 3 : 2"
          filter="url(#mc-pill-shadow)"
        />
        <text :x="n.cx" :y="n.cy + 5" :fill="n.text">
          {{ n.code }}
        </text>
      </g>
    </g>

    <!-- 底部作业模式 -->
    <g text-anchor="middle" font-size="14" fill="#475569">
      <text x="320" y="750">人想</text>
      <text x="320" y="776">人干</text>
      <text x="320" y="802">人改</text>

      <text x="430" y="750">人想</text>
      <text x="430" y="776">机干</text>
      <text x="430" y="802">人大调</text>

      <text x="540" y="750">人想</text>
      <text x="540" y="776">机干</text>
      <text x="540" y="802">人小调</text>

      <text x="650" y="750">人定</text>
      <text x="650" y="776">机干</text>

      <text x="760" y="750">机想</text>
      <text x="760" y="776">机干</text>
      <text x="760" y="802">机自迭代</text>
    </g>
  </svg>
</template>

<script setup>
import { SELF_LEVEL_NODES } from '../data/index.js'

defineProps({
  modelValue: { type: String, default: '' }
})

defineEmits(['select'])

const nodes = SELF_LEVEL_NODES
</script>

<style lang="scss" scoped>
.maturity-chart {
  width: 100%;
  height: auto;
  display: block;
}

.pill {
  cursor: pointer;

  text,
  ellipse {
    transition: all 0.2s ease;
  }

  &:hover ellipse:last-of-type {
    opacity: 0.85;
  }
}
</style>
